import { useState } from "react";
import { Link, createFileRoute, redirect } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { AuthFrame, AuthHead, Notice } from "@/components/AuthFrame";
import { Icon } from "@/components/Icon";
import { CodeInput } from "@/features/auth/CodeInput";
import { authErrorText } from "@/features/auth/errors";
import { signedIn, useGo } from "@/features/auth/afterSignIn";
import { ApiError } from "@/lib/api";
import { meQuery, signIn, signInCode, signInRecoveryCode } from "@/lib/auth";
import { formatTime } from "@/lib/format";
import { validateNextSearch } from "@/lib/next";
import { session } from "@/lib/session";

// Signing in (docs/design_handoff_partner/README.md §6): e-mail and password, then the code from the app —
// or a recovery code for a lost phone, after which a new phone comes first. A wrong address and a wrong
// password read the same (I-m-fout); only who gave the right password hears that the account is locked,
// and until when (I-m-dicht).

export const Route = createFileRoute("/login")({
  validateSearch: validateNextSearch,
  beforeLoad: async ({ context }) => {
    session.ended();
    // Signed in already: straight on.
    const me = await context.queryClient.fetchQuery({ ...meQuery, staleTime: 0 }).catch(() => null);
    if (me) throw redirect({ to: "/shipments" });
  },
  component: LoginScreen,
});

type Step = "password" | "code" | "recovery" | "locked";

function LoginScreen() {
  const { t, i18n } = useTranslation();
  const { next } = Route.useSearch();
  const queryClient = useQueryClient();
  const go = useGo();
  const [step, setStep] = useState<Step>("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [lockedUntil, setLockedUntil] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const fail = (err: unknown) => {
    if (err instanceof ApiError && err.code === "LOGIN_LOCKED") {
      setLockedUntil(err.params.until ?? "");
      setError(null);
      setStep("locked");
      return;
    }
    if (err instanceof ApiError && err.code === "SIGN_IN_AGAIN") {
      setPassword("");
      setStep("password");
    }
    setError(authErrorText(t, err, i18n.language));
  };

  const submitPassword = async () => {
    setBusy(true);
    setError(null);
    try {
      await signIn(email, password);
      setPassword("");
      setCode("");
      setStep("code");
    } catch (err) {
      setPassword("");
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (value = code) => {
    if (value.length !== 6 || busy) return;
    setBusy(true);
    setError(null);
    try {
      void go(signedIn(queryClient, await signInCode(value), next));
    } catch (err) {
      setCode("");
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const submitRecovery = async () => {
    setBusy(true);
    setError(null);
    try {
      void go(signedIn(queryClient, await signInRecoveryCode(recoveryCode), next));
    } catch (err) {
      setRecoveryCode("");
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  if (step === "locked") {
    return (
      <AuthFrame label="Portaal Inloggen dicht">
        <AuthHead title={t("locked.title")} lead={<span className="t-mono">{email}</span>} />
        <Notice tone="warning">{t("locked.text", { time: formatTime(lockedUntil, i18n.language) })}</Notice>
        <p className="t-body-sm t-muted">{t("locked.notYou")}</p>
        <div className="wl-auth__actions">
          <span className="wl-spacer" />
          <button type="button" className="wl-btn wl-btn--secondary wl-btn--lg" onClick={() => setStep("password")}>
            {t("common.backToSignIn")}
          </button>
        </div>
      </AuthFrame>
    );
  }

  if (step === "code") {
    return (
      <AuthFrame label="Portaal Inloggen code">
        <form
          className="wl-vstack"
          style={{ gap: "var(--space-5)", alignItems: "stretch" }}
          onSubmit={(event) => {
            event.preventDefault();
            void submitCode();
          }}
        >
          <AuthHead title={t("code.title")} lead={t("code.lead")} />
          {error && <Notice tone="danger">{error}</Notice>}
          <CodeInput value={code} onChange={setCode} onComplete={(value) => void submitCode(value)} label={t("code.label")} invalid={Boolean(error)} autoFocus />
          <div className="wl-auth__actions">
            <span className="wl-spacer" />
            <button type="submit" className="wl-btn wl-btn--primary wl-btn--lg" disabled={busy || code.length !== 6}>
              {t("login.submit")}
            </button>
          </div>
          <div>
            <button
              type="button"
              className="wl-btn wl-btn--link"
              onClick={() => {
                setError(null);
                setStep("recovery");
              }}
            >
              {t("code.noPhone")}
            </button>
          </div>
        </form>
      </AuthFrame>
    );
  }

  if (step === "recovery") {
    return (
      <AuthFrame label="Portaal Inloggen herstelcode">
        <form
          className="wl-vstack"
          style={{ gap: "var(--space-5)", alignItems: "stretch" }}
          onSubmit={(event) => {
            event.preventDefault();
            void submitRecovery();
          }}
        >
          <div>
            <button type="button" className="wl-btn wl-btn--link" onClick={() => setStep("code")}>
              <Icon name="arrowLeft" />
              {t("recovery.back")}
            </button>
          </div>
          <AuthHead title={t("recovery.title")} lead={t("recovery.lead")} />
          {error && <Notice tone="danger">{error}</Notice>}
          <div className="wl-field">
            <label className="wl-field__label" htmlFor="recovery-code">
              {t("recovery.label")}
            </label>
            <input
              id="recovery-code"
              className="wl-input t-mono"
              value={recoveryCode}
              placeholder="7KQ4-M9TD"
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              autoFocus
              onChange={(event) => setRecoveryCode(event.target.value.toUpperCase())}
            />
          </div>
          <Notice tone="neutral">{t("recovery.thenPhone")}</Notice>
          <div className="wl-auth__actions">
            <span className="wl-spacer" />
            <button type="submit" className="wl-btn wl-btn--primary wl-btn--lg" disabled={busy || recoveryCode.trim().length < 8}>
              {t("login.submit")}
            </button>
          </div>
          <p className="wl-auth__foot">{t("recovery.noneLeft")}</p>
        </form>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame label="Portaal Inloggen">
      <form
        className="wl-vstack"
        style={{ gap: "var(--space-5)", alignItems: "stretch" }}
        onSubmit={(event) => {
          event.preventDefault();
          void submitPassword();
        }}
      >
        <AuthHead title={t("login.title")} lead={t("login.lead")} />
        {error && <Notice tone="danger">{error}</Notice>}
        <div className="wl-field">
          <label className="wl-field__label" htmlFor="login-email">
            {t("login.email")}
          </label>
          <input
            id="login-email"
            className="wl-input"
            type="email"
            autoComplete="username"
            value={email}
            autoFocus
            onChange={(event) => setEmail(event.target.value)}
          />
        </div>
        <div className="wl-field">
          <label className="wl-field__label" htmlFor="login-password">
            {t("login.password")}
          </label>
          <input
            id="login-password"
            className="wl-input"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </div>
        <div className="wl-auth__actions">
          <Link to="/forgot" className="t-body-sm">
            {t("login.forgot")}
          </Link>
          <span className="wl-spacer" />
          <button type="submit" className="wl-btn wl-btn--primary wl-btn--lg" disabled={busy || email.trim() === "" || password === ""}>
            {t("login.submit")}
          </button>
        </div>
        <p className="wl-auth__foot">{t("login.noAccount")}</p>
      </form>
    </AuthFrame>
  );
}
